import { useParams, useNavigate } from 'react-router-dom'
import { Home, LayoutDashboard, Activity, Users, PhoneCall, Sparkles, Building2 } from 'lucide-react'
import Dashboard from './Dashboard'
import HomeDashboard from './home/HomeDashboard'
import OperationsDashboard from './OperationsDashboard'
import DepartmentDashboard from './DepartmentDashboard'
import TeamDashboard from './TeamDashboard'
import CallHistoryDashboard from './CallHistoryDashboard'
import AssistantDashboard from './AssistantDashboard'
import AppLayout from '../layout/AppLayout'
import { useAuthStore } from '../../stores/authStore'
import { visibleDashboardTabs, BASE_TABS, type DashTab } from './dashboardTabs'

const STORAGE_KEY = 'rx2go-dashboard-tab'

const NAV: { tab: DashTab; label: string; icon: React.ReactNode; hint: string; group: 'base' | 'manage' }[] = [
  { tab: 'home', label: 'Home', icon: <Home size={16} />, hint: 'Who reached you, projects, team', group: 'base' },
  { tab: 'today', label: 'Today', icon: <LayoutDashboard size={16} />, hint: "Your day — agenda + what's waiting", group: 'base' },
  { tab: 'operations', label: 'Operations', icon: <Activity size={16} />, hint: 'Orders, finances, comms Gantt', group: 'base' },
  { tab: 'callhistory', label: 'Call History', icon: <PhoneCall size={16} />, hint: 'Every call you took part in', group: 'base' },
  { tab: 'assistant', label: 'Assistant', icon: <Sparkles size={16} />, hint: 'Your AI chats', group: 'base' },
  { tab: 'department', label: 'Department', icon: <Building2 size={16} />, hint: "Your department's day + workload", group: 'manage' },
  { tab: 'team', label: 'Team & Clients', icon: <Users size={16} />, hint: 'Presence, at-risk clients, team feed', group: 'manage' },
]

function getStorageTab(): DashTab {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    if (v && (BASE_TABS as string[]).concat('department', 'team').includes(v)) return v as DashTab
  } catch { /* ignore */ }
  return 'home'
}

export default function Dashboards() {
  const { tab: tabParam } = useParams<{ tab?: string }>()
  const navigate = useNavigate()
  const isAdmin = !!useAuthStore((s) => s.user?.isAdmin)
  const headsDept = (useAuthStore((s) => s.user?.headsDepartments?.length ?? 0)) > 0

  const validTabs = visibleDashboardTabs({ isAdmin, headsDept })
  const storedTab = getStorageTab()
  const resolvedStored: DashTab = validTabs.includes(storedTab) ? storedTab : 'home'
  const tab: DashTab =
    tabParam && validTabs.includes(tabParam as DashTab) ? (tabParam as DashTab) : resolvedStored

  const setActive = (t: DashTab) => {
    try { localStorage.setItem(STORAGE_KEY, t) } catch { /* ignore */ }
    navigate('/dashboards/' + t)
  }

  const items = NAV.filter((n) => validTabs.includes(n.tab))
  const nav = (
    <div className="flex flex-col h-full">
      <div className="h-14 shrink-0 px-4 flex items-center border-b border-border">
        <span className="text-sm font-semibold text-foreground">Dashboards</span>
      </div>
      <nav className="flex-1 min-h-0 overflow-y-auto px-2 py-3 flex flex-col gap-0.5">
        {items.map((n, i) => {
          const prev = items[i - 1]
          const divider = prev && prev.group !== n.group
          return (
            <div key={n.tab}>
              {divider && <div className="my-2 mx-2 h-px bg-border/60" />}
              <button
                onClick={() => setActive(n.tab)}
                title={n.hint}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition duration-150 ${
                  tab === n.tab
                    ? 'bg-muted text-foreground font-medium'
                    : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
                }`}
              >
                {n.icon}
                {n.label}
              </button>
            </div>
          )
        })}
      </nav>
    </div>
  )

  return (
    <AppLayout secondarySidebar={nav}>
      <div className="flex-1 min-w-0 h-full flex flex-col bg-background">
        {tab === 'home' && <HomeDashboard />}
        {tab === 'today' && <Dashboard />}
        {tab === 'operations' && <OperationsDashboard />}
        {tab === 'callhistory' && <CallHistoryDashboard />}
        {tab === 'assistant' && <AssistantDashboard />}
        {tab === 'department' && <DepartmentDashboard />}
        {tab === 'team' && <TeamDashboard />}
      </div>
    </AppLayout>
  )
}
